import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import get from 'lodash/get'
import {
  Column,
  Config,
  DataXEntries,
  getInitWidthColumns,
  SectionConfig,
} from '@microservices/gantt-react-component'
import { getBoxConfig } from '@microservices/gantt-react-component'
import { deepClone } from '@mui/x-data-grid/internals'

import GanttChartRowLazy from '@pages/ConfiduredGantt/GanttChartRowLazy'

import { useExecuteCommandMutation } from '@redux/api'
import { useFetchSeveralTimeLinesDataMutation } from '@redux/api/ganttChart.api'

import { getLocalStorageContext } from '@helpers'
import { FetchGanttChartDataResponse } from '@types'

import { GanttFormValues } from '@gantt/components/GanttCreateOrEdit'
import { JSX_REGISTRY } from '@gantt/constants'

const getStartAndDurationByIndex = (index: number, totalElements: number) => {
  const firstElementStart = 400
  const lastElementEnd = 5600
  const minGap = 100

  if (index < 0 || index >= totalElements) {
    return null
  }

  const availableSpace = lastElementEnd - firstElementStart

  if (totalElements === 1) {
    return {
      _start: firstElementStart,
      _duration: availableSpace,
    }
  }

  const totalGaps = (totalElements - 1) * minGap
  const maxElementSpace = availableSpace - totalGaps

  if (maxElementSpace <= 0) {
    const duration = Math.max(availableSpace / totalElements, 100)
    const gap = 0

    const start = firstElementStart + index * (duration + gap)

    return {
      _start: Math.round(start),
      _duration: Math.round(duration),
    }
  }

  const duration = maxElementSpace / totalElements
  const start = firstElementStart + index * (duration + minGap)

  return {
    _start: Math.round(start),
    _duration: Math.round(duration),
  }
}

export type GanttChartRowsData = FetchGanttChartDataResponse & {
  timelineData: Array<DataXEntries>
}

export type GanttRowPreview = {
  config: GanttFormValues | null
  isConfigValid: boolean | null
}

const GantRowPreview = (params: GanttRowPreview) => {
  const { config: configGanttFormValues, isConfigValid } = params

  const context = getLocalStorageContext()
  const { code } = useParams()
  const { t } = useTranslation()

  const [executeCommand] = useExecuteCommandMutation()
  const [getSeveralTimelinesData] = useFetchSeveralTimeLinesDataMutation()
  const [previewIdsByObject, setPreviewIdsByObject] = useState<{
    [objectCode: string]: { elementId: string | number }
  } | null>(null)
  const [previewDataByObject, setPreviewDataByObject] = useState<{
    [objectCode: string]: GanttChartRowsData
  } | null>(null)
  const [textPlaceholder, setTextPlaceholder] = useState<string | null>(null)

  const config = useMemo<Config | null>(() => {
    return configGanttFormValues ? (deepClone(configGanttFormValues) as Config) : null
  }, [configGanttFormValues])

  const intervals = useMemo(() => {
    const now = new Date()
    const numberOfIntervals = 10

    const startTime = new Date(now)
    startTime.setHours(startTime.getHours() - 4)
    startTime.setMinutes(0, 0, 0)

    return Array.from({ length: numberOfIntervals }, (_, i) => {
      const intervalTime = new Date(startTime)
      intervalTime.setHours(intervalTime.getHours() + i)

      return intervalTime
    })
  }, [])

  //могут быть проблемы если линк в сегментах одного бара будут разными (sections[0])
  const objectsByAxisX = config?.timeline.map(timeline => ({
    id: timeline?.data?.id,
    link: timeline?.sections?.[0]?.link?.axisX?.field?.field,
  }))

  //загрузка id элементов для превью на каждый бар
  useEffect(() => {
    isConfigValid &&
      objectsByAxisX?.forEach(object => {
        if (!object?.id) {
          return
        }

        executeCommand({
          objectCode: object.id,
          commandCode: 'object_read_all',
          method: 'GET',
          size: '1',
          // filter: `${object.link}=isnot=empty`, // обязательное наличие данных ресурса у таймлайна
        })
          .unwrap()
          .then((result: unknown) => {
            // несовместимость результата с CommandResponse[]
            const typedResult = result as { [key: string]: string | number }[]

            setPreviewIdsByObject(prevState => ({
              ...prevState,
              [object.id]: { elementId: typedResult[0]?._id },
            }))
          })
      })
  }, [executeCommand, JSON.stringify(objectsByAxisX), isConfigValid])

  //загрузка данных для превью по id определенных элементов
  useEffect(() => {
    previewIdsByObject &&
      Object.entries(previewIdsByObject).forEach(([key, value]) => {
        code &&
          value.elementId &&
          getSeveralTimelinesData({
            configId: code,
            contextId: context?.id,
            timelineIds: [value.elementId],
            objectCode: key,
          })
            .unwrap()
            .then(result => {
              const transformedResult: GanttChartRowsData = {
                ...result,
                timelineData: result.timelineData.map(item => {
                  const allSegmentConfigs: SectionConfig[] = getBoxConfig(config, item)?.sections

                  const enhancedItems: DataXEntries = {}

                  allSegmentConfigs.forEach((config, segmentIndex) => {
                    const resourceKeyField = config?.link?.resourceLinkField
                    const elemRowId = String(get(item, resourceKeyField))

                    const enhancedItem = {
                      ...item,
                      ...getStartAndDurationByIndex(segmentIndex, allSegmentConfigs.length),
                      _segmentIndex: segmentIndex,
                      _rowId: elemRowId,
                      _level: 0,
                    }

                    if (!enhancedItems[elemRowId]) {
                      enhancedItems[elemRowId] = {}
                    }

                    if (!enhancedItems[elemRowId][segmentIndex]) {
                      enhancedItems[elemRowId][segmentIndex] = {}
                    }

                    enhancedItems[elemRowId][segmentIndex] = enhancedItem
                  })

                  return enhancedItems
                }),
              }

              setPreviewDataByObject(prevState => ({
                ...prevState,
                [key]: transformedResult,
              }))
            })
      })
  }, [previewIdsByObject, code])

  useEffect(() => {
    if (!config || !isConfigValid) {
      setTextPlaceholder(t('ganttCreate.ganttPreview.notValidConfig'))

      return
    }
    if (!previewDataByObject) {
      setTextPlaceholder(t('ganttCreate.ganttPreview.noDataForPreview'))

      return
    }
  }, [previewDataByObject, isConfigValid, config])

  if (!config || !previewDataByObject || !isConfigValid)
    return (
      <div
        style={{
          display: 'flex',
          height: '50px',
          width: 'auto',
          textAlign: 'center',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {textPlaceholder}
      </div>
    )

  const defaultResourcesWidth = 150

  const updatedConfig: Config = {
    ...config,
    canvas: {
      ...config?.canvas,
      yAxisWidth: defaultResourcesWidth,
    },
  }

  const hasRowExpansion = updatedConfig.canvas.hasRowExpansion

  //в обычном случае бэк автоматически фильтрует колонки без видимости
  const columnsForPreview: Column[] =
    updatedConfig?.resource?.columns?.filter(column => column.visibility) ?? []

  const widthColumns = getInitWidthColumns(
    columnsForPreview,
    defaultResourcesWidth,
    hasRowExpansion
  )

  const testTimelineWidthCommon = 1000
  const testGridIndexesCount = 6000

  const rowLayout = {
    rowHeight: updatedConfig.canvas.rowHeight || 50,
    rowLength: testGridIndexesCount,
    topOffset: Math.floor(updatedConfig.canvas.rowHeight * 0.2),
    eventHeight: Math.floor(updatedConfig.canvas.rowHeight * 0.6),
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {Object.entries(previewDataByObject).map(([objectCode, data]) => (
        <div
          key={objectCode}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <GanttChartRowLazy
            key={objectCode}
            config={updatedConfig}
            externalItemsRenderRules={JSX_REGISTRY}
            gridIndexesCount={testGridIndexesCount}
            intervals={intervals}
            isPreview={true}
            isResourcesHidden={{ textReplacement: objectCode }}
            isShownWithEmptyResources={true}
            resourcesWidthCommon={defaultResourcesWidth}
            rowLayout={rowLayout}
            widthColumns={widthColumns}
            dataYEntry={{
              data: data.resourceData?.[0] || { _id: null },
              dataXEntries: data.timelineData[0],
              maxLevel: 0,
            }}
            style={{
              width: `${defaultResourcesWidth + testTimelineWidthCommon}px`,
            }}
          />
        </div>
      ))}
    </div>
  )
}

export default GantRowPreview
